import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { api } from '../services/api';

interface Variant {
  id: number;
  code: string;
  name: string;
}

interface VariantValue {
  id: number;
  variantId: number;
  code: string;
  value: string;
  variantName?: string;
}

const Variants: React.FC = () => {
  const [variants, setVariants] = useState<Variant[]>([]);
  const [variantValues, setVariantValues] = useState<Record<number, VariantValue[]>>({});
  const [loading, setLoading] = useState(false);
  const [variantModalVisible, setVariantModalVisible] = useState(false);
  const [valueModalVisible, setValueModalVisible] = useState(false);
  const [editingVariant, setEditingVariant] = useState<Variant | null>(null);
  const [editingValue, setEditingValue] = useState<VariantValue | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const [variantForm] = Form.useForm();
  const [valueForm] = Form.useForm();

  useEffect(() => {
    loadVariants();
  }, []);

  const loadVariants = async () => {
    try {
      setLoading(true);
      const { data } = await api.get<Variant[]>('/variants');
      setVariants(data);
    } catch (error) {
      message.error('Varyantlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadVariantValues = async (variantId: number) => {
    try {
      const { data } = await api.get<VariantValue[]>(`/variants/values/${variantId}`);
      setVariantValues(prev => ({ ...prev, [variantId]: data }));
    } catch (error) {
      message.error('Varyant değerleri yüklenirken hata oluştu');
    }
  };

  const handleAddVariant = () => {
    setEditingVariant(null);
    variantForm.resetFields();
    setVariantModalVisible(true);
  };

  const handleEditVariant = (variant: Variant) => {
    setEditingVariant(variant);
    variantForm.setFieldsValue(variant);
    setVariantModalVisible(true);
  };

  const handleDeleteVariant = async (id: number) => {
    try {
      await api.delete(`/variants/${id}`);
      message.success('Varyant başarıyla silindi');
      loadVariants();
    } catch (error) {
      message.error('Varyant silinirken hata oluştu');
    }
  };

  const handleAddValue = (variantId: number) => {
    setEditingValue(null);
    setSelectedVariantId(variantId);
    valueForm.resetFields();
    valueForm.setFieldsValue({ variantId });
    setValueModalVisible(true);
  };

  const handleEditValue = (value: VariantValue) => {
    setEditingValue(value);
    setSelectedVariantId(value.variantId);
    valueForm.setFieldsValue(value);
    setValueModalVisible(true);
  };

  const handleDeleteValue = async (id: number, variantId: number) => {
    try {
      await api.delete(`/variants/values/${id}`);
      message.success('Varyant değeri başarıyla silindi');
      await loadVariantValues(variantId);
    } catch (error) {
      message.error('Varyant değeri silinirken hata oluştu');
    }
  };

  const handleVariantModalOk = async () => {
    try {
      const values = await variantForm.validateFields();
      
      if (editingVariant) {
        await api.put(`/variants/${editingVariant.id}`, { id: editingVariant.id, ...values });
        message.success('Varyant başarıyla güncellendi');
      } else {
        await api.post('/variants', values);
        message.success('Varyant başarıyla oluşturuldu');
      }
      
      setVariantModalVisible(false);
      loadVariants();
    } catch (error) {
      message.error('İşlem sırasında hata oluştu');
    }
  };

  const handleValueModalOk = async () => {
    try {
      const values = await valueForm.validateFields();
      
      if (editingValue) {
        await api.put(`/variants/values/${editingValue.id}`, { id: editingValue.id, ...values });
        message.success('Varyant değeri başarıyla güncellendi');
      } else {
        await api.post('/variants/values', values);
        message.success('Varyant değeri başarıyla oluşturuldu');
      }
      
      setValueModalVisible(false);
      if (selectedVariantId) {
        await loadVariantValues(selectedVariantId);
      }
    } catch (error) {
      message.error('İşlem sırasında hata oluştu');
    }
  };

  const variantColumns = [
    {
      title: 'Varyant Kodu',
      dataIndex: 'code',
      key: 'code',
      width: 150,
    },
    {
      title: 'Varyant Adı',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Değerler',
      key: 'values',
      render: (_: any, record: Variant) => {
        const values = variantValues[record.id] || [];
        return values.length > 0 ? values.map(v => v.value).join(', ') : '-';
      }
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: Variant) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditVariant(record)}
          />
          <Button
            type="default"
            size="small"
            onClick={() => {
              loadVariantValues(record.id);
              handleAddValue(record.id);
            }}
          >
            Değer Ekle
          </Button>
          <Popconfirm
            title="Bu varyantı silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteVariant(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h1>Varyantlar</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddVariant}>
          Yeni Varyant
        </Button>
      </div>

      <Table
        columns={variantColumns}
        dataSource={variants}
        rowKey="id"
        loading={loading}
        expandable={{
          onExpand: (expanded, record) => {
            // Satır expand edildiğinde varyant değerlerini yükle
            if (expanded) {
              loadVariantValues(record.id);
            }
          },
          expandedRowRender: (record) => {
            const values = variantValues[record.id] || [];
            
            if (values.length === 0) {
              return <div style={{ padding: 16 }}>Değer yok</div>;
            }

            const valueColumns = [
              {
                title: 'Kod',
                dataIndex: 'code',
                key: 'code',
                width: 120,
              },
              {
                title: 'Değer',
                dataIndex: 'value',
                key: 'value',
              },
              {
                title: 'İşlemler',
                key: 'actions',
                render: (_: any, v: VariantValue) => (
                  <Space>
                    <Button
                      type="primary"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEditValue(v)}
                    />
                    <Popconfirm
                      title="Bu değeri silmek istediğinizden emin misiniz?"
                      onConfirm={() => handleDeleteValue(v.id, v.variantId)}
                      okText="Evet"
                      cancelText="Hayır"
                    >
                      <Button
                        type="primary"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  </Space>
                ),
              },
            ];

            return (
              <Table
                columns={valueColumns}
                dataSource={values}
                rowKey="id"
                size="small"
                pagination={false}
              />
            );
          },
        }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Toplam ${total} varyant`,
        }}
      />

      <Modal
        title={editingVariant ? 'Varyant Düzenle' : 'Yeni Varyant'}
        open={variantModalVisible}
        onOk={handleVariantModalOk}
        onCancel={() => setVariantModalVisible(false)}
        width={500}
      >
        <Form form={variantForm} layout="vertical">
          <Form.Item
            name="code"
            label="Varyant Kodu"
            rules={[{ required: true, message: 'Varyant kodu gerekli' }]}
          >
            <Input placeholder="Örn: COLOR, SIZE" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Varyant Adı"
            rules={[{ required: true, message: 'Varyant adı gerekli' }]}
          >
            <Input placeholder="Örn: Renk, Beden" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingValue ? 'Varyant Değeri Düzenle' : 'Yeni Varyant Değeri'}
        open={valueModalVisible}
        onOk={handleValueModalOk}
        onCancel={() => setValueModalVisible(false)}
        width={500}
      >
        <Form form={valueForm} layout="vertical">
          <Form.Item name="variantId" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item
            name="code"
            label="Kod"
            rules={[{ required: true, message: 'Kod gerekli' }]}
          >
            <Input placeholder="Örn: RED, M" />
          </Form.Item>
          <Form.Item
            name="value"
            label="Değer"
            rules={[{ required: true, message: 'Değer gerekli' }]}
          >
            <Input placeholder="Örn: Kırmızı, M" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Variants;

